/**
 * Binds a search result to the search that produced it.
 * A later search, a different product, or a stale response must not replace it.
 * Cache stores (price matrix, hotel profile, images) are not touched here.
 */

export const DESTINATION_ALL = "ALL";
export const HOTELS_PRODUCT = "hotels";
export const PACKAGES_PRODUCT = "FlightsHotel";
export const FLIGHT_HOTEL_PRODUCT = "FlightHotelCompose";
export const DYNAMIC_PACKAGES_PRODUCT = "DynamicPackages";

export const BOOKING_BLOCKED_MESSAGE =
  "לא ניתן להמשיך להזמנה — התוצאה אינה תואמת לחיפוש הפעיל";

function primaryLocation(location) {
  if (Array.isArray(location)) return location[0] || null;
  return location && typeof location === "object" ? location : null;
}

function normalizeToken(value) {
  return String(value ?? "").trim().toLowerCase();
}

function isAllDestinationsLabel(value) {
  const text = String(value ?? "").trim();
  if (!text) return false;
  const upper = text.toUpperCase();
  return upper === "ALL" || upper === "ALL DESTINATIONS" || text === "כל היעדים";
}

function isAllDestinationsFlightLocation(location) {
  if (location == null) return false;
  if (typeof location === "string") return isAllDestinationsLabel(location);
  const loc = primaryLocation(location);
  if (!loc) return false;
  const cityCode = String(loc.CITYCODE ?? "").trim().toUpperCase();
  const airportCode = String(loc.AIRPORTCODE ?? "").trim().toUpperCase();
  if (cityCode === "ALL" || airportCode === "ALL") return true;
  return (
    isAllDestinationsLabel(loc.CITYNAME) ||
    isAllDestinationsLabel(loc.CITYNAME_HEB) ||
    isAllDestinationsLabel(loc.fullname) ||
    isAllDestinationsLabel(loc.COUNTRYNAME)
  );
}

function normalizeSearchResultDate(value) {
  if (value == null) return null;
  const raw = String(value).trim().split(/\s+/)[0] || "";
  if (!raw) return null;
  const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  const ldsMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ldsMatch) {
    const [, day, month, year] = ldsMatch;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return raw;
}

export function destinationKeyFromLdsCity(city) {
  const raw = String(city ?? "").trim();
  if (!raw) return DESTINATION_ALL;
  if (raw.toUpperCase() === "ALL" || raw === "כל היעדים") return DESTINATION_ALL;
  return raw.toUpperCase();
}

export function destinationKeyFromLocation(location) {
  if (location == null || location === "") return "";
  if (typeof location === "string") {
    if (isAllDestinationsFlightLocation(location)) return DESTINATION_ALL;
    return normalizeToken(location);
  }
  const loc = primaryLocation(location);
  if (!loc) return "";
  if (isAllDestinationsFlightLocation(loc)) return DESTINATION_ALL;
  const code = String(loc.AIRPORTCODE || loc.CITYCODE || loc.iata || loc.flightIata || "")
    .trim()
    .toUpperCase();
  if (code && code !== "ALL") return code;
  const name = String(loc.CITYNAME || loc.city || loc.CITYNAME_HEB || loc.fullname || loc.label || "").trim();
  return normalizeToken(name);
}

export function destinationKeyFromPhrase(phrase) {
  if (!phrase || typeof phrase !== "object") return "";
  if (phrase.destinationKey) return String(phrase.destinationKey);
  const code = String(phrase.flightIata || phrase.iata || phrase.AIRPORTCODE || phrase.CITYCODE || "")
    .trim()
    .toUpperCase();
  if (code === "ALL") return DESTINATION_ALL;
  if (code) return code;
  const city = String(phrase.city || phrase.CITYNAME || "").trim();
  if (!city) return "";
  const lower = city.toLowerCase();
  if (lower === "all" || lower === "all destinations" || city === "כל היעדים") {
    return DESTINATION_ALL;
  }
  return lower;
}

/**
 * Flight+hotel compose follows the active package location.
 * A previous hotel searchPhrase must not choose the city.
 */
export function resolveComposeSearchTarget({ location, searchPhrase = null, searchTab = "" } = {}) {
  const destinationKey = destinationKeyFromLocation(location);
  const phraseKey = destinationKeyFromPhrase(searchPhrase);
  const phraseProduct = searchPhrase?.productType || (searchPhrase ? HOTELS_PRODUCT : null);
  const ignoredPhrase = Boolean(
    phraseKey && (phraseKey !== destinationKey || (phraseProduct && phraseProduct !== searchTab))
  );

  if (!destinationKey) {
    return {
      ok: false,
      reason: "missing_destination",
      destinationKey: "",
      ignoredPhrase,
      composePhrase: null,
    };
  }
  if (destinationKey === DESTINATION_ALL) {
    return {
      ok: false,
      reason: "all_destinations_skips_single_city_compose",
      destinationKey,
      ignoredPhrase,
      composePhrase: null,
    };
  }

  const loc = primaryLocation(location) || {};
  return {
    ok: true,
    reason: "active_location",
    destinationKey,
    ignoredPhrase,
    composePhrase: {
      city: loc.CITYNAME || loc.city || loc.CITYNAME_HEB || loc.fullname || "",
      country: loc.COUNTRYNAME || loc.country || loc.COUNTRYNAME_HEB || "",
      countryCode: loc.COUNTRYCODE || loc.countryCode || "",
      flightIata: loc.AIRPORTCODE || loc.CITYCODE || loc.iata || "",
      productType: searchTab || null,
      destinationKey,
    },
  };
}

export function buildActiveSearchIdentity({
  searchId = null,
  productType = null,
  parentProduct = null,
  destinationKey = "",
  checkIn = null,
  checkOut = null,
  occupancySignature = null,
} = {}) {
  return {
    searchId: searchId ?? null,
    productType: productType || null,
    parentProduct: parentProduct || productType || null,
    destinationKey: destinationKey || "",
    checkIn: normalizeSearchResultDate(checkIn),
    checkOut: normalizeSearchResultDate(checkOut),
    occupancySignature: occupancySignature || null,
  };
}

function productsCompatible(rowIdentity, active) {
  const rowProduct = rowIdentity?.productType || null;
  const parent = rowIdentity?.parentProduct || rowProduct;
  const activeProduct = active?.productType || null;
  if (!rowProduct || !activeProduct) return false;
  if (rowProduct === activeProduct) return true;
  return parent === activeProduct;
}

export function identityMatches(rowIdentity, active, options = {}) {
  if (!rowIdentity || !active) return false;
  if (rowIdentity.searchId == null || active.searchId == null) return false;
  if (String(rowIdentity.searchId) !== String(active.searchId)) return false;
  if (!productsCompatible(rowIdentity, active)) return false;
  if (
    rowIdentity.destinationKey &&
    active.destinationKey &&
    rowIdentity.destinationKey !== active.destinationKey
  ) {
    return false;
  }
  if (rowIdentity.checkIn && active.checkIn && rowIdentity.checkIn !== active.checkIn) return false;
  if (rowIdentity.checkOut && active.checkOut && rowIdentity.checkOut !== active.checkOut) return false;
  if (
    options.occupancy === true &&
    rowIdentity.occupancySignature &&
    active.occupancySignature &&
    rowIdentity.occupancySignature !== active.occupancySignature
  ) {
    return false;
  }
  return true;
}

export function stampSearchRows(rows, identity) {
  if (!Array.isArray(rows) || !identity) return rows;
  return rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    return {
      ...row,
      __searchIdentity: identity,
      __searchRequestId: identity.searchId,
    };
  });
}

export function shouldCommitSearchResponse({
  responseSearchId = null,
  activeSearchId = null,
  responseProduct = null,
  activeProduct = null,
} = {}) {
  if (responseSearchId == null || activeSearchId == null) {
    return { accept: false, reason: "missing_search_id" };
  }
  if (String(responseSearchId) !== String(activeSearchId)) {
    return { accept: false, reason: "stale_search_id" };
  }
  if (responseProduct && activeProduct && responseProduct !== activeProduct) {
    return { accept: false, reason: "product_mismatch" };
  }
  return { accept: true, reason: "match" };
}

export function isHubwayzHotelTabRow(row) {
  if (!row || typeof row !== "object" || row.fullDeal) return false;
  const provider = String(row.provider || row._supplier || row.supplier || "").toLowerCase();
  if (provider !== "hubwayz") return false;
  if (row._productKind === "flight_hotel" || row.productKind === "flight_hotel") return false;
  return Boolean(row.DisplayName || row.HotelName || (row.hubwayzHotelId && row.ID));
}

export function isUnstampedFlightHotelCard(row) {
  if (!row || typeof row !== "object" || row.fullDeal || row.__searchIdentity) return false;
  if (row._productKind === "flight_hotel" || row.productKind === "flight_hotel") return true;
  return row.provider === "hubwayz" && row.hotelName != null && !row.DisplayName;
}

function isHotelProduct(active) {
  return (
    active?.productType === HOTELS_PRODUCT || active?.productType === DYNAMIC_PACKAGES_PRODUCT
  );
}

export function rowVisibleForActiveSearch(row, active) {
  if (!row || !active?.searchId) return false;
  const hotelProduct = isHotelProduct(active);
  if (isHubwayzHotelTabRow(row) && !hotelProduct) return false;
  if (isUnstampedFlightHotelCard(row)) return false;
  if (row.__searchIdentity) {
    if (String(row.__searchIdentity.searchId) !== String(active.searchId)) return false;
    return productsCompatible(row.__searchIdentity, active);
  }
  if (
    row.__searchRequestId != null &&
    String(row.__searchRequestId) !== String(active.searchId)
  ) {
    return false;
  }
  const hotelShaped = !row.fullDeal && !row.tour;
  if (hotelShaped && !hotelProduct) return false;
  if (!hotelShaped && hotelProduct) return false;
  return true;
}

export function packageRowsForActiveSearch(rows, active) {
  const list = Array.isArray(rows) ? rows : [];
  if (!active) return [];
  return list.filter((row) => rowVisibleForActiveSearch(row, active));
}

export function flightHotelBundleMatchesActiveSearch(bundle, active) {
  if (!bundle || !Array.isArray(bundle.cards) || !active) return false;
  if (!bundle.__searchIdentity) return false;
  return identityMatches(bundle.__searchIdentity, active);
}

export function evaluateBookingEligibility(row, active) {
  if (!active?.searchId) return { allow: false, reason: "missing_active_search" };
  if (!row || typeof row !== "object") return { allow: false, reason: "missing_result" };
  const hotelProduct = isHotelProduct(active);
  if (isHubwayzHotelTabRow(row) && !hotelProduct) {
    return { allow: false, reason: "hotel_result_on_other_product" };
  }
  if (row.__searchIdentity) {
    return identityMatches(row.__searchIdentity, active, { occupancy: true })
      ? { allow: true, reason: "identity_match" }
      : { allow: false, reason: "identity_mismatch" };
  }
  if (isUnstampedFlightHotelCard(row)) {
    return { allow: false, reason: "unstamped_flight_hotel" };
  }
  if (row.__searchRequestId != null) {
    if (String(row.__searchRequestId) !== String(active.searchId)) {
      return { allow: false, reason: "stale_search_id" };
    }
    return { allow: true, reason: "request_id_match" };
  }
  const hotelShaped = !row.fullDeal && !row.tour;
  if (hotelShaped) return { allow: false, reason: "unstamped_result" };
  if (row.fullDeal && hotelProduct) {
    return { allow: false, reason: "package_result_on_hotel_search" };
  }
  if (row.fullDeal) return { allow: true, reason: "legacy_package_deal" };
  return { allow: false, reason: "unstamped_result" };
}

export function phraseBelongsToTab(phrase, tab) {
  if (!phrase || typeof phrase !== "object") return false;
  const product = phrase.productType || null;
  if (!product) return tab === HOTELS_PRODUCT;
  return product === tab;
}

export function isolateStateOnProductChange({ previousProduct, nextProduct, phrase = null } = {}) {
  const productChanged = previousProduct !== nextProduct;
  return {
    productChanged,
    bumpSearchId: productChanged,
    clearResults: productChanged,
    clearComposeBundle: productChanged,
    dropPhrase: productChanged && !phraseBelongsToTab(phrase, nextProduct),
    clearCaches: false,
  };
}
